"use client";

import { HistoryItem, SupportedLanguage } from "../types";

interface RecentHistoryProps {
  history: HistoryItem[];
  onSelect: (item: HistoryItem) => void;
}

const LANG_SHORT: Record<SupportedLanguage, string> = {
  javascript: "JS",
  php: "PHP",
  python: "PY",
  java: "JV",
  sql: "SQL",
  other: "?",
};

const LANG_COLORS: Record<SupportedLanguage, string> = {
  javascript: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  php: "text-indigo-400 bg-indigo-400/10 border-indigo-400/20",
  python: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  java: "text-orange-400 bg-orange-400/10 border-orange-400/20",
  sql: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  other: "text-zinc-400 bg-zinc-400/10 border-zinc-400/20",
};

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

export default function RecentHistory({ history, onSelect }: RecentHistoryProps) {
  if (history.length === 0) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="px-4 py-3.5 border-b border-zinc-800 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-500">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <h3 className="text-sm font-semibold text-zinc-300">Recent</h3>
        </div>
        <div className="px-4 py-8 text-center">
          <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center mx-auto mb-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-600">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <p className="text-zinc-600 text-xs leading-5">
            Explained errors will appear here for quick access
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="px-4 py-3.5 border-b border-zinc-800 flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-500">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        <h3 className="text-sm font-semibold text-zinc-300">Recent</h3>
        <span className="ml-auto text-xs text-zinc-600 bg-zinc-800 px-1.5 py-0.5 rounded">
          {history.length}
        </span>
      </div>
      <div className="divide-y divide-zinc-800/60 max-h-72 overflow-y-auto">
        {history.map((item) => (
          <button
            key={item.id}
            onClick={() => onSelect(item)}
            className="w-full text-left px-4 py-3 hover:bg-zinc-800/40 transition-colors duration-150 group"
          >
            <div className="flex items-start gap-2 mb-1">
              <span
                className={`flex-shrink-0 text-xs font-bold px-1.5 py-0.5 rounded border ${LANG_COLORS[item.language]}`}
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px" }}
              >
                {LANG_SHORT[item.language]}
              </span>
              <span className="text-xs text-zinc-500 ml-auto">{timeAgo(item.timestamp)}</span>
            </div>
            <p
              className="text-xs text-zinc-400 group-hover:text-zinc-300 transition-colors leading-4 truncate"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {item.errorSnippet}
            </p>
            <p className="text-xs text-zinc-600 mt-0.5">
              {item.explanation.errorType}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}